import { components, paths } from "@api/api";
import useAxios from "@hooks/useFetch";
import { useMetric } from "@core/CommonDataContext";
import { useMemo } from "react";
import {
    MetricForTable,
    Metric,
    VISUALIZE_METRICS_ORDER,
    useMapMetricsForTable,
} from "@models/Metric";
import { KeywordBase, useGetKeywordBaseData } from "@api/useGetKeywordBaseData";

const VISUALIZE_DATA_ENDPOINT: keyof paths = "/api/Visualize/Get";

export type VisualizeDataRequest = components["schemas"]["FlavorTracker.Entities.DTO.Common.AmongDropDownBaseRequest"];
export type VisualizeDataResponseRawItem = components["schemas"]["FlavorTracker.Entities.DTO.VisualizePage.VisualizePageResponse"];
export type VisualizeDataResponseRaw = VisualizeDataResponseRawItem[];

const FAMILIAR_FAVORITES_KNOW_IT_NORM_THRESHOLD = 75;
const RISING_STARS_KNOW_IT_CHANGE_NORM_THRESHOLD = 75;
const FAMILIAR_FAVORITES_LOVE_LIKE_IT_NORM_THRESHOLD = 75;
const RISING_STARS_LOVE_IT_NORM_THRESHOLD = 50;
const POLARITY_LOVE_IT_NORM_THRESHOLD = 75;
const POLARITY_HATE_IT_NORM_THRESHOLD = 75;

export enum ArtificialMetric {
    KnowItChange = "KnowItChange",
}

export type VisualizeMetric = Metric | ArtificialMetric;

export function useVisualizeMetric(metric?: VisualizeMetric): {
    id?: VisualizeMetric;
    name?: string;
} | null {
    const originalMetric = useMetric(metric);

    if (metric === ArtificialMetric.KnowItChange) {
        return {
            id: ArtificialMetric.KnowItChange,
            name: 'Know It Chg',
        };
    }

    return originalMetric ?? null;
}

function getMetricsCombinationScore (norm1: number | null, norm2: number | null, hasMultiplier: boolean): number | null {
    if (norm1 === null || norm2 === null) {
        return null;
    }
    return (norm1 + norm2) * (hasMultiplier ? 1000 : 1);
}

export type VisualizeDataItemMetric = Omit<MetricForTable, 'id'> & {
    id: VisualizeMetric;
};

export type VisualizeDataItem = Omit<VisualizeDataResponseRawItem, 'id' | 'metrics'> & KeywordBase & {
    heatScoreNum: number | null;
    metrics: VisualizeDataItemMetric[];
    isPolarValue: boolean;
    polarityScore: number;
    polarityRaw: number;

    isFamiliarFavorites: boolean;
    familiarFavoritesNum: number | null;

    isRisingStars: boolean;
    risingStarsNum: number | null;
};

export type VisualizeData = VisualizeDataItem[];

export const useVisualizeData = (payload: VisualizeDataRequest) => {
    const getKeywordBaseData = useGetKeywordBaseData();
    const mapMetricsForTable = useMapMetricsForTable(VISUALIZE_METRICS_ORDER);

    const response = useAxios<VisualizeDataResponseRaw, VisualizeDataRequest>({
        url: VISUALIZE_DATA_ENDPOINT,
        payload,
        method: 'post',
    });

    const data = useMemo(
        (): VisualizeDataItem[] => {
            return (response.data ?? []).map((item) => {
                const id = item.id!;
                const keywordBase = getKeywordBaseData(id);
                const polarityMetric = item.metrics?.find(i => i.metric === Metric.PolarityScore);
                const loveItMetric = item.metrics?.find(i => i.metric === Metric.LoveIt);
                const hateItMetric = item.metrics?.find(i => i.metric === Metric.HateIt);
                const knowItMetric = item.metrics?.find(i => i.metric === Metric.KnowIt);
                const loveLikeItMetric = item.metrics?.find(i => i.metric === Metric.LoveLikeIt);

                const knowItChange = knowItMetric?.change ?? null;
                const knowItChangeSign = (knowItMetric?.change ?? 0) > 0 ? '+' : '';
                const knowItOriginalChange = knowItMetric?.originalChange ?? null;

                const isPolarValue = (loveItMetric?.norm ?? 0) >= POLARITY_LOVE_IT_NORM_THRESHOLD
                    && (hateItMetric?.norm ?? 0) >= POLARITY_HATE_IT_NORM_THRESHOLD;

                const isFamiliarFavorites = (knowItMetric?.norm ?? 0) >= FAMILIAR_FAVORITES_KNOW_IT_NORM_THRESHOLD
                    && (loveLikeItMetric?.norm ?? 0) >= FAMILIAR_FAVORITES_LOVE_LIKE_IT_NORM_THRESHOLD;

                const familiarFavoritesNum = getMetricsCombinationScore(
                    Math.max(knowItMetric?.originalRaw ?? 0, knowItMetric?.raw ?? 0),
                    Math.max(loveLikeItMetric?.originalRaw ?? 0, loveLikeItMetric?.raw ?? 0),
                    isFamiliarFavorites,
                );

                const isRisingStars = (knowItMetric?.normChange ?? 0) >= RISING_STARS_KNOW_IT_CHANGE_NORM_THRESHOLD
                    && (loveItMetric?.norm ?? 0) <= RISING_STARS_LOVE_IT_NORM_THRESHOLD;

                const risingStarsNum = knowItOriginalChange === null ? null : getMetricsCombinationScore(
                    knowItOriginalChange,
                    0,
                    isRisingStars,
                );


                return {
                    ...item,
                    ...keywordBase,
                    heatScoreNum: (keywordBase?.heatScore && knowItOriginalChange !== null)
                        ? getMetricsCombinationScore(
                            keywordBase?.heatScore,
                            knowItOriginalChange,
                            true
                        )
                        : null,
                    metrics: [
                        ...mapMetricsForTable(item.metrics ?? []),
                        {
                            id: ArtificialMetric.KnowItChange,
                            name: 'Know It Chg',
                            tableColName: 'Know It 2 Yr Change',
                            tableValue: knowItChange === null ? '-' : `${knowItChangeSign}${knowItChange}%`,
                            copyTableValue: knowItChange === null ? '' : `${knowItChange}%`,
                            value: knowItChange,
                            sortValue: knowItOriginalChange,
                            norm: knowItMetric?.normChange ?? 0,
                        }
                    ],
                    isPolarValue,
                    polarityScore: polarityMetric?.norm ?? 0,
                    polarityRaw: polarityMetric?.originalRaw ?? 0,

                    isFamiliarFavorites,
                    familiarFavoritesNum,

                    isRisingStars: isRisingStars,
                    risingStarsNum,
                };
            });
        },
        [response.data, getKeywordBaseData, mapMetricsForTable]
    );

    return { ...response, data };
};
